// frontend/src/components/staff/scheduleBuilderUtils.js
//
// Workforce — SCHED-SLICE1-E admin Schedule Builder helpers.
//
// Pure functions backing the weekly drag-and-drop calendar at
// /staff/schedule. Loosely coupled to the Shift + Availability schemas
// (locked in docs/architecture/workforce-scheduling-tech.md §"Shift" and
// §"Availability") consumed via `GET /api/workforce/shifts`.
//
// All timestamps render in America/Phoenix per CLAUDE.md global rule.
// All times labeled "MST" explicitly.
//
// Role-color vocabulary MUST stay aligned with the staff schedule view
// (SCHED-SLICE1-G `ROLE_COLOR_MAP`) so cross-surface consistency (visual
// spec §12) holds.

import moment from "moment-timezone";

export const STUDIO_TZ = "America/Phoenix";
export const STUDIO_TZ_LABEL = "MST";

// Grid bounds (visual spec §3.1).
export const DEFAULT_GRID_START_HOUR = 6;  // 6am
export const DEFAULT_GRID_END_HOUR = 22;   // 10pm (exclusive)
export const HOUR_ROW_HEIGHT_PX = 48;
export const SNAP_MINUTES = 15;
export const DRAG_PICKUP_MS = 150;

/**
 * Role-color descriptor — mirrors SCHED-SLICE1-G `ROLE_COLOR_MAP` for
 * cross-surface consistency (visual spec §3.2 + §9.2 + §12).
 *
 * Slice 1 ships three role codes; "other" is the fallback for forward-compat.
 */
export const ROLE_COLOR_MAP = Object.freeze({
  front_desk: {
    key: "front_desk",
    label: "Front Desk",
    bg: "#2F855A",
    text: "#ffffff",
  },
  massage_therapist: {
    key: "massage_therapist",
    label: "Massage Therapist",
    bg: "#1F5C63",
    text: "#ffffff",
  },
  other: {
    key: "other",
    label: "Other",
    bg: "#553C9A",
    text: "#ffffff",
  },
});

export const getRoleColor = (roleCode) =>
  ROLE_COLOR_MAP[roleCode] || ROLE_COLOR_MAP.other;

export const formatRoleLabel = (roleCode) => {
  if (!roleCode) return "Shift";
  const known = ROLE_COLOR_MAP[roleCode];
  if (known) return known.label;
  return String(roleCode)
    .split("_")
    .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ""))
    .join(" ")
    .trim();
};

/**
 * Week math (Phoenix-MST). Visual spec §3.5 uses Mon-Sun pictographic
 * examples but the underlying tech doc §"Conflict detection" uses
 * Sunday week-start (matching `moment().startOf('week')` default).
 *
 * SCHED-SLICE1-G consolidated on Sunday-start; we mirror that for
 * cross-surface consistency.
 */
export const computeWeekStart = (reference) => {
  const ref = reference ? moment.tz(reference, STUDIO_TZ) : moment.tz(STUDIO_TZ);
  if (!ref.isValid()) {
    return moment.tz(STUDIO_TZ).startOf("week").format("YYYY-MM-DD");
  }
  return ref.startOf("week").format("YYYY-MM-DD");
};

export const computeWeekRange = (weekStartYmd) => {
  const start = moment.tz(weekStartYmd, "YYYY-MM-DD", STUDIO_TZ).startOf("day");
  const end = start.clone().add(7, "days");
  return {
    fromIso: start.toISOString(),
    toIso: end.toISOString(),
    fromMoment: start,
    endMoment: end,
  };
};

export const stepWeek = (weekStartYmd, direction) => {
  const base = moment.tz(weekStartYmd, "YYYY-MM-DD", STUDIO_TZ);
  if (!base.isValid()) return computeWeekStart();
  return base.add(direction * 7, "days").format("YYYY-MM-DD");
};

export const formatWeekRangeLabel = (weekStartYmd) => {
  const start = moment.tz(weekStartYmd, "YYYY-MM-DD", STUDIO_TZ);
  if (!start.isValid()) return "";
  const end = start.clone().add(6, "days");
  if (start.month() === end.month()) {
    return `${start.format("MMM D")}-${end.format("D, YYYY")}`;
  }
  return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
};

/**
 * Build the 7 day-column descriptors for the visible week.
 */
export const buildWeekDays = (weekStartYmd) => {
  const start = moment.tz(weekStartYmd, "YYYY-MM-DD", STUDIO_TZ);
  if (!start.isValid()) return [];
  const today = moment.tz(STUDIO_TZ).startOf("day");
  return Array.from({ length: 7 }, (_, i) => {
    const day = start.clone().add(i, "days");
    return {
      iso: day.format("YYYY-MM-DD"),
      dayOfWeek: day.day(),
      weekday: day.format("ddd"),
      monthLabel: day.format("MMM"),
      dayLabel: day.format("D"),
      isToday: day.isSame(today, "day"),
      isPast: day.isBefore(today, "day"),
    };
  });
};

/**
 * Build the hour-row descriptors for the visible grid. Range is inclusive
 * of `startHour`, exclusive of `endHour`.
 */
export const buildHourRows = (startHour = DEFAULT_GRID_START_HOUR, endHour = DEFAULT_GRID_END_HOUR) => {
  const rows = [];
  for (let h = startHour; h < endHour; h++) {
    rows.push({
      hour: h,
      label: moment({ hour: h, minute: 0 }).format("h A"),
    });
  }
  return rows;
};

/**
 * Snap a minute-of-day to the nearest SNAP_MINUTES boundary.
 * `mode` controls rounding: 'floor' / 'round' / 'ceil'.
 */
export const snapMinutes = (minuteOfDay, mode = "round") => {
  if (mode === "floor") return Math.floor(minuteOfDay / SNAP_MINUTES) * SNAP_MINUTES;
  if (mode === "ceil") return Math.ceil(minuteOfDay / SNAP_MINUTES) * SNAP_MINUTES;
  return Math.round(minuteOfDay / SNAP_MINUTES) * SNAP_MINUTES;
};

/**
 * Convert a Y-pixel offset within the calendar grid to a snapped minute-of-day,
 * given the first visible hour (e.g. 6 = 6am).
 */
export const yPxToMinuteOfDay = (yPx, startHour = DEFAULT_GRID_START_HOUR, mode = "round") => {
  const minutesFromTop = (yPx / HOUR_ROW_HEIGHT_PX) * 60;
  const minuteOfDay = startHour * 60 + minutesFromTop;
  const snapped = snapMinutes(minuteOfDay, mode);
  return Math.max(0, Math.min(24 * 60, snapped));
};

/**
 * Convert a (dayIso, minute-of-day) pair to an ISO 8601 timestamp anchored
 * to Phoenix-MST. The minute-of-day may exceed 1440 (handled by moment).
 */
export const dayAndMinuteToIso = (dayIso, minuteOfDay) => {
  const base = moment.tz(dayIso, "YYYY-MM-DD", STUDIO_TZ).startOf("day");
  return base.clone().add(minuteOfDay, "minutes").toISOString();
};

/**
 * Compute the rendering geometry for a shift block within the grid.
 * Returns `null` if the shift falls fully outside the visible hour range.
 */
export const computeShiftBlockGeometry = (shift, startHour = DEFAULT_GRID_START_HOUR, endHour = DEFAULT_GRID_END_HOUR) => {
  if (!shift || !shift.startTime || !shift.endTime) return null;
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  const end = moment.tz(shift.endTime, STUDIO_TZ);
  if (!start.isValid() || !end.isValid() || end.isSameOrBefore(start)) return null;

  const dayBase = start.clone().startOf("day");
  const visibleStartMinute = startHour * 60;
  const visibleEndMinute = endHour * 60;

  const shiftStartMinute = start.diff(dayBase, "minutes");
  const shiftEndMinute = end.diff(dayBase, "minutes");

  // Fully outside the visible window.
  if (shiftEndMinute <= visibleStartMinute) return null;
  if (shiftStartMinute >= visibleEndMinute) return null;

  // Clip to visible window.
  const clippedStart = Math.max(shiftStartMinute, visibleStartMinute);
  const clippedEnd = Math.min(shiftEndMinute, visibleEndMinute);

  const topPx = ((clippedStart - visibleStartMinute) / 60) * HOUR_ROW_HEIGHT_PX;
  const heightPx = ((clippedEnd - clippedStart) / 60) * HOUR_ROW_HEIGHT_PX;

  return {
    dayIso: start.format("YYYY-MM-DD"),
    topPx,
    heightPx,
    isClippedTop: shiftStartMinute < visibleStartMinute,
    isClippedBottom: shiftEndMinute > visibleEndMinute,
  };
};

/**
 * Group shifts by ISO date for column-rendering convenience.
 */
export const groupShiftsByDay = (shifts) => {
  const out = new Map();
  if (!Array.isArray(shifts)) return out;
  for (const shift of shifts) {
    if (!shift || !shift.startTime) continue;
    const dayIso = moment.tz(shift.startTime, STUDIO_TZ).format("YYYY-MM-DD");
    if (!out.has(dayIso)) out.set(dayIso, []);
    out.get(dayIso).push(shift);
  }
  return out;
};

// ---------------------------------------------------------------------------
// SCHED-OVERLAP-COLUMNS — overlap detection + parallel-column layout helpers.
//
// Visual spec: docs/design/SCHED-OVERLAP-COLUMNS-design.md §1, §2, §3.
// Story:       docs/backlog/SCHED-OVERLAP-COLUMNS.md (Scenarios 1-11).
//
// Detection rule (half-open interval per spec §1.1):
//     A.start < B.end  AND  B.start < A.end
//
// Back-to-back shifts (A.end === B.start) do NOT overlap — matches BE
// conflict-detection precedent and keeps sequential shifts at full width.
//
// Grouping (spec §1.2):
//     Transitive closure via a sweep. Each shift joins any existing group
//     whose running `maxEndMinute` > the shift's `startMinute`; otherwise
//     it starts a new group. Sort order = `createdAt` ASC then `_id` ASC
//     (spec §3.1, §3.3 — stable across renames / cancellations).
//
// Bail floor (spec §5.4):
//     Column layout becomes the stacked-row fallback when group size N
//     reaches 4+ OR the consumer reports columnWidth < 40px. The helper
//     emits enough info (N + columnIndex) for the parent grid to make
//     the per-render call against measured pixel widths.
// ---------------------------------------------------------------------------

const INTERNAL_MS_PER_MIN = 60 * 1000;

/**
 * Convert a shift to (startMinute, endMinute) in absolute epoch minutes.
 * Using epoch minutes (not "minute-of-day") so cross-midnight or DST
 * edge cases never fold incorrectly — overlap is defined on the same
 * day cell (parent groups by `dayIso` before calling this), and inside
 * the cell the absolute timeline is monotonic.
 */
const shiftToMinuteRange = (shift) => {
  if (!shift || !shift.startTime || !shift.endTime) return null;
  const startMs = new Date(shift.startTime).getTime();
  const endMs = new Date(shift.endTime).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  if (endMs <= startMs) return null;
  return {
    startMin: startMs / INTERNAL_MS_PER_MIN,
    endMin: endMs / INTERNAL_MS_PER_MIN,
  };
};

/**
 * Compare two shifts for deterministic column-index assignment.
 * Order: createdAt ASC, then _id ASC (Mongo ObjectId is time-ordered, so
 * the _id tiebreak is effectively a finer-grained timestamp).
 */
const compareShiftsForColumnOrder = (a, b) => {
  const aCreatedRaw = a && a.createdAt ? new Date(a.createdAt).getTime() : NaN;
  const bCreatedRaw = b && b.createdAt ? new Date(b.createdAt).getTime() : NaN;
  const aCreated = Number.isFinite(aCreatedRaw) ? aCreatedRaw : Number.POSITIVE_INFINITY;
  const bCreated = Number.isFinite(bCreatedRaw) ? bCreatedRaw : Number.POSITIVE_INFINITY;
  if (aCreated !== bCreated) return aCreated - bCreated;
  const aId = a && a._id ? String(a._id) : "";
  const bId = b && b._id ? String(b._id) : "";
  if (aId < bId) return -1;
  if (aId > bId) return 1;
  return 0;
};

/**
 * Detect overlap groups for the shifts in a single day cell.
 *
 * Returns a Map keyed by `shift._id` (or, defensively, the array index as
 * a string when `_id` is missing for transient new-shift objects). Each
 * value is `{ columnIndex: number, groupSize: number }` per spec §2.1 +
 * §3.1.
 *
 * Special cases (spec + Scenario 11 unit-coverage list):
 *   - Empty / non-array input -> empty Map (no crash).
 *   - Single shift / multiple non-overlapping shifts -> each gets
 *     `{ columnIndex: 0, groupSize: 1 }` (full-width fallback per §2.4).
 *   - Two fully or partially overlapping shifts -> one group of size 2
 *     with column-indices [0, 1] by createdAt ASC.
 *   - Transitive: A overlaps B + B overlaps C (A and C don't directly
 *     overlap) -> single group of size 3.
 *   - Cancelled shifts are INCLUDED in the grouping (per §4 — column
 *     positioning is orthogonal to cancelled styling).
 */
export const computeOverlapGroups = (shifts) => {
  const out = new Map();
  if (!Array.isArray(shifts) || shifts.length === 0) return out;

  // Stable sort by createdAt ASC, then _id ASC.
  const sorted = shifts.slice().sort(compareShiftsForColumnOrder);

  // Each group: { members: shift[], maxEndMin: number }
  const groups = [];

  for (const shift of sorted) {
    const range = shiftToMinuteRange(shift);
    if (!range) {
      // Defensive: invalid shifts get their own singleton group so the
      // grid still renders them at full width without crashing.
      groups.push({ members: [shift], maxEndMin: -Infinity });
      continue;
    }
    // Find an existing group whose maxEndMin > this shift's startMin.
    // (Half-open interval rule — back-to-back never overlaps.)
    let joined = false;
    for (const group of groups) {
      if (group.maxEndMin > range.startMin) {
        group.members.push(shift);
        if (range.endMin > group.maxEndMin) group.maxEndMin = range.endMin;
        joined = true;
        break;
      }
    }
    if (!joined) {
      groups.push({ members: [shift], maxEndMin: range.endMin });
    }
  }

  // Emit per-shift {columnIndex, groupSize}.
  for (const group of groups) {
    const groupSize = group.members.length;
    group.members.forEach((shift, idx) => {
      const key = shift && shift._id ? String(shift._id) : `__anon_${out.size}`;
      out.set(key, { columnIndex: idx, groupSize });
    });
  }

  return out;
};

/**
 * Configuration thresholds for the column-width legibility ladder.
 * Per visual spec §5.1 + §5.4. Exported as a frozen object so QA + parent
 * grid can reference identical constants without re-deriving the cliffs.
 */
export const OVERLAP_LAYOUT_THRESHOLDS = Object.freeze({
  STACK_ROW_MIN_COLUMN_PX: 40, // < 40px -> bail to stacked-row layout
  MINIMAL_MIN_COLUMN_PX: 40,   // [40, 60) -> Minimal mode
  COMPACT_MIN_COLUMN_PX: 60,   // [60, 80) -> Compact mode
  STANDARD_MIN_COLUMN_PX: 80,  // >= 80   -> Standard mode
  STACK_ROW_GROUP_SIZE: 4,     // N >= 4 -> bail to stacked-row (combined w/ width gate)
  INTER_COLUMN_GAP_PX: 2,      // spec §2.2
  DAY_COLUMN_INNER_PCT: 0.9,   // 90% inner (5% left + 5% right gutter)
  DAY_COLUMN_LEFT_GUTTER_PCT: 0.05,
});

/**
 * Select the text-rendering mode for a given measured column width.
 *
 * Returns one of "standard" | "compact" | "minimal". The < 40px tier is
 * handled by `shouldBailToStackRow` (the caller renders a different
 * layout entirely), so this helper does NOT emit a "bail" mode — at the
 * sub-40px threshold the caller has already taken the stacked-row branch.
 */
export const selectTextMode = (columnWidthPx) => {
  // Coerce: null / undefined / NaN -> "standard" (safe default for the
  // pre-measurement render; Number(null) === 0 so the explicit nullish
  // gate must come before the Number() coercion).
  if (columnWidthPx === null || columnWidthPx === undefined) return "standard";
  const w = Number(columnWidthPx);
  if (!Number.isFinite(w)) return "standard";
  if (w >= OVERLAP_LAYOUT_THRESHOLDS.STANDARD_MIN_COLUMN_PX) return "standard";
  if (w >= OVERLAP_LAYOUT_THRESHOLDS.COMPACT_MIN_COLUMN_PX) return "compact";
  return "minimal";
};

/**
 * Decide whether an overlap group's column layout should bail to the
 * stacked-row fallback (spec §5.4). Two triggers:
 *   - groupSize >= 4 OR
 *   - per-column width falls under STACK_ROW_MIN_COLUMN_PX.
 *
 * Caller passes the *measured* per-column width (already computed via
 * `computeOverlapColumnWidth`). When the dayCellWidth is unknown (e.g.,
 * before the first ResizeObserver tick), default to no-bail so the
 * initial render is at least column-layout — the next layout effect
 * corrects to stacked-row if the cell is too narrow.
 */
export const shouldBailToStackRow = ({ groupSize, columnWidthPx }) => {
  if (Number.isFinite(groupSize) && groupSize >= OVERLAP_LAYOUT_THRESHOLDS.STACK_ROW_GROUP_SIZE) {
    return true;
  }
  if (Number.isFinite(columnWidthPx) && columnWidthPx < OVERLAP_LAYOUT_THRESHOLDS.STACK_ROW_MIN_COLUMN_PX) {
    return true;
  }
  return false;
};

/**
 * Compute the per-column pixel width for an overlap group.
 *
 *     columnWidth = (W_inner - GAP * (N - 1)) / N
 *
 * where W_inner = dayCellWidth * DAY_COLUMN_INNER_PCT (the 90% inner band
 * after the 5% gutters from slice-1 §3.1). Returns 0 when dayCellWidth
 * is not yet measured (caller must guard against the rendering side
 * effects of a zero column width — typically by skipping the bail check
 * until the next layout effect).
 */
export const computeOverlapColumnWidth = (dayCellWidthPx, groupSize) => {
  const w = Number(dayCellWidthPx);
  const n = Number(groupSize);
  if (!Number.isFinite(w) || w <= 0) return 0;
  if (!Number.isFinite(n) || n < 1) return 0;
  const inner = w * OVERLAP_LAYOUT_THRESHOLDS.DAY_COLUMN_INNER_PCT;
  if (n === 1) return inner;
  const gapTotal = OVERLAP_LAYOUT_THRESHOLDS.INTER_COLUMN_GAP_PX * (n - 1);
  return Math.max(0, (inner - gapTotal) / n);
};

/**
 * Format a shift's time range in the compact form ("8am-3pm"). MST suffix
 * is dropped per spec §5.2 — page-level convention carries the timezone.
 */
export const formatShiftTimeRangeCompact = (shift) => {
  if (!shift || !shift.startTime || !shift.endTime) return "";
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  const end = moment.tz(shift.endTime, STUDIO_TZ);
  if (!start.isValid() || !end.isValid()) return "";
  const fmt = (m) => {
    const mins = m.minutes();
    const hr = m.format("h");
    const ap = m.format("a"); // "am" / "pm"
    return mins === 0 ? `${hr}${ap}` : `${hr}:${m.format("mm")}${ap}`;
  };
  return `${fmt(start)}-${fmt(end)}`;
};

/**
 * Format a shift's time range in the minimal form ("8a-3p"). For the
 * narrowest legible column widths (40-59px) per spec §5.2. MST suffix
 * dropped.
 */
export const formatShiftTimeRangeMinimal = (shift) => {
  if (!shift || !shift.startTime || !shift.endTime) return "";
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  const end = moment.tz(shift.endTime, STUDIO_TZ);
  if (!start.isValid() || !end.isValid()) return "";
  const fmt = (m) => {
    const mins = m.minutes();
    const hr = m.format("h");
    const apFull = m.format("a"); // "am" / "pm"
    const ap = apFull.charAt(0);  // "a" / "p"
    return mins === 0 ? `${hr}${ap}` : `${hr}:${m.format("mm")}${ap}`;
  };
  return `${fmt(start)}-${fmt(end)}`;
};

/**
 * Compress a staff display name to "F. LastName" for minimal-mode rendering
 * (spec §5.1). Defensive on missing parts — falls back to the full name.
 * The block CSS handles the ellipsis truncation visually; this helper just
 * reduces the source string so the ellipsis lands on the surname, not on
 * the first name.
 */
export const formatStaffNameMinimal = (fullName) => {
  if (typeof fullName !== "string" || fullName.trim().length === 0) return "";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const last = parts.slice(1).join(" ");
  if (!first || !last) return fullName;
  return `${first.charAt(0).toUpperCase()}. ${last}`;
};

/**
 * Format a shift's time range according to the text-rendering mode.
 * Wrapper that delegates to the existing `formatShiftTimeRange` (Standard,
 * with " MST" suffix) or the compact / minimal forms above.
 */
export const formatShiftTimeRangeForMode = (shift, mode) => {
  if (mode === "compact") return formatShiftTimeRangeCompact(shift);
  if (mode === "minimal") return formatShiftTimeRangeMinimal(shift);
  return formatShiftTimeRange(shift);
};

/**
 * Format a shift's MST time range for display, e.g. "8:00am - 2:00pm MST".
 */
export const formatShiftTimeRange = (shift) => {
  if (!shift) return "";
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  const end = moment.tz(shift.endTime, STUDIO_TZ);
  if (!start.isValid() || !end.isValid()) return "";
  return `${start.format("h:mma")} - ${end.format("h:mma")} ${STUDIO_TZ_LABEL}`;
};

/**
 * Compute a shift's duration in hours (decimal).
 */
export const shiftDurationHours = (shift) => {
  if (!shift) return 0;
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  const end = moment.tz(shift.endTime, STUDIO_TZ);
  if (!start.isValid() || !end.isValid()) return 0;
  return Math.max(0, end.diff(start, "minutes") / 60);
};

/**
 * Aggregate per-staff weekly hours from the visible-week shift list.
 *
 * Returns an array of summary rows ready to render in the weekly hour
 * summary footer (visual spec §3.6). Rows are sorted alphabetically by
 * staff name.
 *
 * Each row:
 *   - staffProfileId, name, role, hours, limit (nullable), pctOfLimit,
 *     status: 'ok' | 'approaching' | 'over'
 *
 * `approaching` fires at >= 90% of limit (and limit > 0); `over` fires at
 * > 100%. Null limit -> always 'ok' (display "X / -- hrs").
 */
export const computeWeeklyHourSummary = (shifts, staffById) => {
  const totals = new Map();
  if (!Array.isArray(shifts)) return [];
  for (const shift of shifts) {
    if (!shift || !shift.staffProfileId || shift.status === "cancelled") continue;
    const hours = shiftDurationHours(shift);
    const prior = totals.get(shift.staffProfileId) || 0;
    totals.set(shift.staffProfileId, prior + hours);
  }

  const rows = [];
  for (const [staffProfileId, hours] of totals.entries()) {
    const profile = staffById && staffById.get ? staffById.get(staffProfileId) : null;
    const limit = profile && typeof profile.weeklyHourLimit === "number" ? profile.weeklyHourLimit : null;
    const name = resolveStaffDisplayName(profile);
    const role = profile && profile.primaryRole ? profile.primaryRole : "other";
    let status = "ok";
    let pctOfLimit = null;
    if (limit !== null && limit > 0) {
      pctOfLimit = (hours / limit) * 100;
      if (hours > limit) status = "over";
      else if (pctOfLimit >= 90) status = "approaching";
    }
    rows.push({
      staffProfileId,
      name,
      role,
      hours: Math.round(hours * 10) / 10,
      limit,
      pctOfLimit,
      status,
    });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name));
  return rows;
};

/**
 * Build a staff-id -> staff descriptor map from the calendar query response.
 * The BE returns staff with populated `userId` ({ firstName, lastName, ... })
 * per workforceShiftsController.listShifts.
 */
export const indexStaffById = (staffArray) => {
  const out = new Map();
  if (!Array.isArray(staffArray)) return out;
  for (const profile of staffArray) {
    if (!profile || !profile._id) continue;
    out.set(String(profile._id), profile);
  }
  return out;
};

export const resolveStaffDisplayName = (profile) => {
  if (!profile) return "Unknown staff";
  const u = profile.userId && typeof profile.userId === "object" ? profile.userId : null;
  if (u && (u.firstName || u.lastName)) {
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unknown staff";
  }
  if (profile.displayName) return profile.displayName;
  if (profile.name) return profile.name;
  return "Unknown staff";
};

/**
 * Pre-flight availability check — FE-side HARD BLOCK policy per visual
 * spec §10.1. Returns a conflict descriptor when the shift falls outside
 * ALL of the staff's active availability windows for that dayOfWeek, OR
 * when the staff has NO availability records at all.
 *
 * Mirrors the BE shape (workforce-scheduling-tech.md §"Conflict response
 * shape (canonical)") so the FE can reject pre-POST without round-trip.
 *
 * @returns {null | { type, message, shiftDate, shiftStartHHMM, shiftEndHHMM }}
 */
export const preValidateAvailability = ({ staffProfileId, startIso, endIso, availability }) => {
  const start = moment.tz(startIso, STUDIO_TZ);
  const end = moment.tz(endIso, STUDIO_TZ);
  if (!start.isValid() || !end.isValid() || end.isSameOrBefore(start)) return null;

  const shiftDay = start.format("YYYY-MM-DD");
  const dayOfWeek = start.day();
  const shiftStartMin = start.hours() * 60 + start.minutes();
  const shiftEndMin = end.hours() * 60 + end.minutes();
  const shiftStartHHMM = start.format("HH:mm");
  const shiftEndHHMM = end.format("HH:mm");

  const records = Array.isArray(availability)
    ? availability.filter((a) => String(a.staffProfileId) === String(staffProfileId))
    : [];

  if (records.length === 0) {
    return {
      type: "NO_AVAILABILITY_RECORDS",
      message:
        "Staff has not set availability yet. Ask them to add availability before assigning shifts.",
      shiftDate: shiftDay,
      shiftStartHHMM,
      shiftEndHHMM,
    };
  }

  const shiftMoment = start.clone().startOf("day");

  const fullyCoveringWindow = records.find((rec) => {
    if (rec.dayOfWeek !== dayOfWeek) return false;

    // effectiveDate <= shiftDay
    if (rec.effectiveDate) {
      const eff = moment.tz(rec.effectiveDate, STUDIO_TZ);
      if (eff.isValid() && eff.isAfter(shiftMoment, "day")) return false;
    }
    // endDate == null OR endDate >= shiftDay
    if (rec.endDate) {
      const endD = moment.tz(rec.endDate, STUDIO_TZ);
      if (endD.isValid() && endD.isBefore(shiftMoment, "day")) return false;
    }

    const [sH, sM] = String(rec.startTime || "").split(":").map((n) => Number(n));
    const [eH, eM] = String(rec.endTime || "").split(":").map((n) => Number(n));
    if (Number.isNaN(sH) || Number.isNaN(eH)) return false;
    const recStartMin = sH * 60 + (sM || 0);
    const recEndMin = eH * 60 + (eM || 0);
    return recStartMin <= shiftStartMin && recEndMin >= shiftEndMin;
  });

  if (fullyCoveringWindow) return null;

  return {
    type: "OUTSIDE_AVAILABILITY",
    message: `Staff is not marked available on ${start.format("ddd MMM D")} from ${start.format("h:mma")} to ${end.format("h:mma")} ${STUDIO_TZ_LABEL}.`,
    shiftDate: shiftDay,
    shiftStartHHMM,
    shiftEndHHMM,
  };
};

/**
 * NEW-SHIFT-AVAILABILITY-PEEK — Filter the in-prop availability list for the
 * subset of windows that ACTIVELY APPLY to (staffProfileId, dateYmd). A window
 * applies when:
 *   - record.staffProfileId matches
 *   - record.dayOfWeek matches the dayOfWeek of `dateYmd` in America/Phoenix
 *   - record.effectiveDate <= dateYmd (when set)
 *   - record.endDate is null OR record.endDate >= dateYmd
 *
 * Returns a sorted array (by startTime ASC) ready for visual rendering.
 *
 * Per Zuri spec §3 the mini-strip surfaces both recurring and one-off windows
 * (one-off support is gated on SCHED-AVAIL-DASHBOARD-002-B data shape — `isRecurring:
 * false` records carry an `effectiveDate === endDate` single-day span; this
 * helper treats them identically because both representations fold into the
 * same effectiveDate/endDate gating logic above).
 *
 * @param {{ staffProfileId: string, dateYmd: string, availability: Array }} args
 * @returns {Array<{ _id?, dayOfWeek, startTime, endTime, isRecurring? }>}
 */
export const getAvailabilityWindowsForDate = ({ staffProfileId, dateYmd, availability }) => {
  if (!staffProfileId || !dateYmd || !Array.isArray(availability)) return [];
  const target = moment.tz(dateYmd, "YYYY-MM-DD", STUDIO_TZ).startOf("day");
  if (!target.isValid()) return [];
  const targetDow = target.day();

  const matches = availability.filter((rec) => {
    if (!rec || String(rec.staffProfileId) !== String(staffProfileId)) return false;
    if (rec.dayOfWeek !== targetDow) return false;
    if (rec.effectiveDate) {
      const eff = moment.tz(rec.effectiveDate, STUDIO_TZ);
      if (eff.isValid() && eff.isAfter(target, "day")) return false;
    }
    if (rec.endDate) {
      const endD = moment.tz(rec.endDate, STUDIO_TZ);
      if (endD.isValid() && endD.isBefore(target, "day")) return false;
    }
    return true;
  });

  // Sort by startTime ASC for stable visual rendering.
  return matches.sort((a, b) => {
    const aStart = String(a.startTime || "");
    const bStart = String(b.startTime || "");
    if (aStart < bStart) return -1;
    if (aStart > bStart) return 1;
    return 0;
  });
};

/**
 * NEW-SHIFT-AVAILABILITY-PEEK — Determine whether a staff member has ANY
 * availability records at all (recurring or one-off, any day). Used by the
 * mini-strip to distinguish State B (no avail this day) from State C
 * (no records at all).
 *
 * @returns {boolean}
 */
export const hasAnyAvailabilityRecords = ({ staffProfileId, availability }) => {
  if (!staffProfileId || !Array.isArray(availability)) return false;
  return availability.some((rec) => rec && String(rec.staffProfileId) === String(staffProfileId));
};

// ---------------------------------------------------------------------------
// SCHED-SAME-ROLE-OVERLAP-WARN — Soft-warning helper for the create/edit-time
// inline banner. Surfaces when a proposed (or being-edited) shift overlaps
// another active shift on the SAME Phoenix-MST calendar date AND SAME role.
//
// Story:       docs/backlog/SCHED-SAME-ROLE-OVERLAP-WARN.md (13 AC scenarios)
// Tech doc:    docs/architecture/sched-same-role-overlap-warn-tech.md
// Visual spec: docs/design/SCHED-SAME-ROLE-OVERLAP-WARN-design.md §1.1
//
// Detection rule (half-open interval — verbatim from SCHED-OVERLAP-COLUMNS
// §1.1 / matches BE conflict-detection precedent):
//     overlap = S.start < X.end  AND  X.start < S.end
//
// Scope of match:
//   - Same Phoenix-MST calendar date (compared via `ymdInPhoenix(startTime)`)
//   - Same role (string equality after trim + case-fold per visual spec §1.2)
//   - Existing shift status !== 'cancelled' (per spec §1.5 / Scenario 6)
//   - When `editingShiftId` provided, that shift is excluded (per Scenario 7)
//
// Returns an ordered array of "conflict descriptors" — one per overlapping
// shift, sorted ascending by startTime so the banner's bulleted list reads
// chronologically. Each descriptor carries enough info for the banner to
// render the verbatim copy from the visual spec §3.3 without re-querying.
// ---------------------------------------------------------------------------

const ymdInPhoenix = (isoLike) => {
  const m = moment.tz(isoLike, STUDIO_TZ);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const normalizeRole = (role) =>
  String(role == null ? "" : role).trim().toLowerCase();

/**
 * Detect existing shifts that overlap with the proposed shift on the same
 * Phoenix-MST date + same role. Pure function — safe to call on every
 * render or every keystroke (caller debounces per visual spec §1.6).
 *
 * @param {Object} args
 * @param {Object} args.proposed - the shift being created/edited
 *   { startTime, endTime, roleCode }
 * @param {string} [args.editingShiftId] - in EDIT mode, the `_id` of the
 *   shift being edited (excluded from the candidate set per Scenario 7)
 * @param {Array} args.shifts - the visible-week shift array from the
 *   parent ScheduleBuilder (TanStack-cached; no new endpoint per
 *   Marcus's tech doc §"Approach")
 * @param {Map} [args.staffById] - optional id→profile map for resolving
 *   the conflicting shift's display name (per Scenario 8: unassigned →
 *   "Unassigned"; assigned → resolved name)
 *
 * @returns {Array<{
 *   _id: string,
 *   staffProfileId: string|null,
 *   staffDisplayName: string,
 *   startTime: string,
 *   endTime: string,
 *   roleCode: string,
 * }>} Empty array when nothing overlaps. Sorted by startTime ASC.
 */
export const detectSameRoleOverlapShifts = ({
  proposed,
  editingShiftId,
  shifts,
  staffById,
}) => {
  if (!proposed || !proposed.startTime || !proposed.endTime || !proposed.roleCode) {
    return [];
  }
  if (!Array.isArray(shifts) || shifts.length === 0) return [];

  const proposedStartMs = new Date(proposed.startTime).getTime();
  const proposedEndMs = new Date(proposed.endTime).getTime();
  if (
    !Number.isFinite(proposedStartMs) ||
    !Number.isFinite(proposedEndMs) ||
    proposedEndMs <= proposedStartMs
  ) {
    return [];
  }

  const proposedYmd = ymdInPhoenix(proposed.startTime);
  if (!proposedYmd) return [];

  const proposedRole = normalizeRole(proposed.roleCode);
  if (!proposedRole) return [];

  const editingId = editingShiftId ? String(editingShiftId) : null;

  const matches = [];
  for (const shift of shifts) {
    if (!shift || !shift.startTime || !shift.endTime) continue;
    // Scenario 6: cancelled shifts are out.
    if (shift.status === "cancelled") continue;
    // Scenario 7: a shift never warns about itself.
    if (editingId && String(shift._id) === editingId) continue;
    // §1.2: same role, opaque string equality (trim + case-fold).
    if (normalizeRole(shift.roleCode) !== proposedRole) continue;
    // §1.3: same calendar date (Phoenix-MST).
    if (ymdInPhoenix(shift.startTime) !== proposedYmd) continue;
    // §1.1: half-open interval overlap.
    const sStartMs = new Date(shift.startTime).getTime();
    const sEndMs = new Date(shift.endTime).getTime();
    if (!Number.isFinite(sStartMs) || !Number.isFinite(sEndMs)) continue;
    if (sEndMs <= sStartMs) continue;
    const overlaps = proposedStartMs < sEndMs && sStartMs < proposedEndMs;
    if (!overlaps) continue;

    let displayName = "Unassigned";
    if (shift.staffProfileId) {
      const profile =
        staffById && typeof staffById.get === "function"
          ? staffById.get(String(shift.staffProfileId))
          : null;
      if (profile) {
        displayName = resolveStaffDisplayName(profile);
      } else {
        // Cache miss — keep a stable fallback so the banner still renders.
        displayName = "Staff member";
      }
    }

    matches.push({
      _id: shift._id ? String(shift._id) : "",
      staffProfileId: shift.staffProfileId ? String(shift.staffProfileId) : null,
      staffDisplayName: displayName,
      startTime: shift.startTime,
      endTime: shift.endTime,
      roleCode: shift.roleCode,
    });
  }

  // Sort by startTime ASC for chronological banner ordering (visual spec §3.3
  // multi-overlap bulleted list reads earliest-first).
  matches.sort((a, b) => {
    const aMs = new Date(a.startTime).getTime();
    const bMs = new Date(b.startTime).getTime();
    if (aMs !== bMs) return aMs - bMs;
    return String(a._id).localeCompare(String(b._id));
  });

  return matches;
};

/**
 * Format a shift's time range for the SCHED-SAME-ROLE-OVERLAP-WARN banner.
 * Per visual spec §3.3:
 *   - "8:30 AM - 12:30 PM" with single space around the hyphen
 *   - NO MST suffix (modal-level context declares MST per spec §1.3)
 *   - No leading zeros on the hour
 */
export const formatBannerShiftTimeRange = (shift) => {
  if (!shift || !shift.startTime || !shift.endTime) return "";
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  const end = moment.tz(shift.endTime, STUDIO_TZ);
  if (!start.isValid() || !end.isValid()) return "";
  return `${start.format("h:mm A")} - ${end.format("h:mm A")}`;
};

/**
 * Format a Phoenix-MST date as "Sat May 16" (3-letter weekday + month, day-
 * of-month, NO year) for the SCHED-SAME-ROLE-OVERLAP-WARN banner primary
 * line. Visual spec §3.3.
 */
export const formatBannerDayLabel = (isoLike) => {
  const m = moment.tz(isoLike, STUDIO_TZ);
  if (!m.isValid()) return "";
  return m.format("ddd MMM D");
};

/**
 * Resolve the proposed-shift staff first-name for the banner primary line.
 * Defensive on missing inputs — falls back to empty when the profile is
 * unresolvable so the caller can substitute a stable placeholder. Keeps the
 * banner copy grammatical when admin hasn't yet picked a staff member.
 */
export const resolveStaffFirstName = (profile) => {
  if (!profile) return "";
  const u = profile.userId && typeof profile.userId === "object" ? profile.userId : null;
  if (u && u.firstName) return String(u.firstName).trim();
  const full = resolveStaffDisplayName(profile);
  if (!full || full === "Unknown staff") return "";
  return full.split(/\s+/)[0] || "";
};

/**
 * Resolve the conflicting-shift's display first-name from the descriptor
 * returned by `detectSameRoleOverlapShifts`. Mirrors visual spec §3.3 single-
 * overlap copy: "[Staff first name] overlaps with [Other Staff first name]'s
 * ...". When the conflicting shift is unassigned, returns "Unassigned" (per
 * Scenario 8).
 */
export const firstNameFromConflictDescriptor = (descriptor) => {
  if (!descriptor) return "";
  if (!descriptor.staffProfileId) return "Unassigned";
  const name = String(descriptor.staffDisplayName || "").trim();
  if (!name) return "Staff member";
  return name.split(/\s+/)[0] || name;
};

/**
 * Validate a shift form payload — gates the Save CTA in the Edit Shift modal.
 *
 * @returns {{ ok: true } | { ok: false, code, message, field? }}
 */
export const validateShiftFormPayload = ({ staffProfileId, startIso, endIso, roleCode }) => {
  if (!staffProfileId) {
    return { ok: false, code: "MISSING_STAFF", message: "Pick a staff member.", field: "staffProfileId" };
  }
  if (!roleCode) {
    return { ok: false, code: "MISSING_ROLE", message: "Pick a role.", field: "roleCode" };
  }
  const start = moment.tz(startIso, STUDIO_TZ);
  const end = moment.tz(endIso, STUDIO_TZ);
  if (!start.isValid() || !end.isValid()) {
    return { ok: false, code: "INVALID_TIMES", message: "Start and end time are required." };
  }
  if (end.isSameOrBefore(start)) {
    return { ok: false, code: "END_BEFORE_START", message: "End time must be after start time.", field: "endTime" };
  }
  // Slice 1 simplification (tech doc §"Edge case — shift spans midnight"):
  // shift must start and end on the same Phoenix-MST calendar day.
  if (!start.isSame(end, "day")) {
    return {
      ok: false,
      code: "MULTI_DAY_SHIFT",
      message: "Shift must start and end on the same day. Use two shifts for overnight coverage.",
      field: "endTime",
    };
  }
  return { ok: true };
};

/**
 * Compute the visible-week shifts after a hypothetical create/edit/delete
 * has been applied. Used for instant FE feedback while the network round-
 * trip is in flight.
 */
export const applyOptimisticShiftMutation = (shifts, mutation) => {
  if (!mutation) return shifts;
  const list = Array.isArray(shifts) ? shifts.slice() : [];
  if (mutation.type === "create") {
    list.push(mutation.shift);
  } else if (mutation.type === "update") {
    const idx = list.findIndex((s) => String(s._id) === String(mutation.shift._id));
    if (idx >= 0) list[idx] = { ...list[idx], ...mutation.shift };
  } else if (mutation.type === "delete") {
    const idx = list.findIndex((s) => String(s._id) === String(mutation.shiftId));
    if (idx >= 0) list.splice(idx, 1);
  }
  return list;
};

/**
 * Build the ARIA label for a shift block (visual spec §17 plus
 * SCHED-CANCELLED-VISUAL-ADMIN §11 for the cancelled-state aria-label).
 *
 * For cancelled shifts the aria-label leads with "Cancelled shift for ..."
 * and folds the cancellation reason in so screen readers convey the
 * full forensic context. Defensive on a missing cancelReason — falls
 * through to "no reason recorded".
 */
export const buildShiftAriaLabel = (shift, staffById) => {
  if (!shift) return "Shift";
  const profile = staffById && staffById.get ? staffById.get(String(shift.staffProfileId)) : null;
  const name = resolveStaffDisplayName(profile);
  const role = formatRoleLabel(shift.roleCode);
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  const end = moment.tz(shift.endTime, STUDIO_TZ);
  const isCancelled = shift.status === "cancelled";
  if (!start.isValid() || !end.isValid()) {
    if (isCancelled) return `Cancelled ${role} shift for ${name}`;
    return `${role} shift for ${name}`;
  }
  const dayLabel = start.format("dddd MMMM D");
  const range = `${start.format("h:mma")} to ${end.format("h:mma")}`;
  if (isCancelled) {
    const rawReason = typeof shift.cancelReason === "string" ? shift.cancelReason.trim() : "";
    const reasonClause = rawReason ? `Reason: ${rawReason}.` : "No reason recorded.";
    return `Cancelled ${role} shift for ${name}, ${dayLabel}, ${range} ${STUDIO_TZ_LABEL}. ${reasonClause} Press Enter to view details.`;
  }
  const statusLabel = shift.status === "draft" ? "draft" : "published";
  return `${role} shift for ${name}, ${dayLabel}, ${range} ${STUDIO_TZ_LABEL}, status ${statusLabel}`;
};

/**
 * Detect whether the visible-week list contains at least one draft shift
 * (controls the Publish button enable/label per visual spec §6.1).
 */
export const computePublishCtaState = (shifts) => {
  if (!Array.isArray(shifts) || shifts.length === 0) {
    return { enabled: false, label: "Publish week", reason: "no_shifts" };
  }
  const draftCount = shifts.filter((s) => s && s.status === "draft").length;
  if (draftCount === 0) {
    return { enabled: false, label: "Published", reason: "no_drafts" };
  }
  return { enabled: true, label: `Publish week (${draftCount})`, reason: "has_drafts" };
};

/**
 * Build a stable signature for the visible-week shift list. Used by polling
 * to detect drift between successive fetches (so we can highlight the
 * "another admin changed something" cue — visual spec §3 polling cadence).
 */
export const buildShiftsSignature = (shifts) => {
  if (!Array.isArray(shifts) || shifts.length === 0) return "empty:0";
  const parts = shifts
    .map((s) => {
      if (!s || !s._id) return "";
      return `${s._id}:${s.startTime || ""}:${s.endTime || ""}:${s.status || ""}:${s.staffProfileId || ""}:${s.roleCode || ""}`;
    })
    .filter(Boolean);
  parts.sort();
  return `count:${parts.length}|${parts.join("|")}`;
};
